import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import createMutationsSharer from "./vuex-shared-mutations";
import { getDefaultState } from "./state";
import { getters as generalGetters } from "./getters";
import { actions as generalActions } from "./actions";
import { mutations as generalMutations } from "./mutations";

import { eventCategorySetup } from "./event-category-setup/index";
import { armDisarm } from "./arm-disarm-area/index";
import { eventqueue } from "./eventqueue/index";
import { siteMonitor } from "./site-monitor/index";
import { siteMonitorCameras } from "./site-monitor-cameras/index";
import { tasks } from "./tasks/index";
import { sessions } from "./sessions/index";
import { postLoginAppBootstrap } from "./plugins";
import { mapLayers } from "./map-layers/index";
import { dispatch } from "./dispatch/index";
import { userManagement } from "./user-management/index";
import { ldapManagement } from "./ldap-management/index";
import { devices } from "./devices/index";
import { eventSearch } from "./event-search/index";
import { alarmPoints } from "./alarm-points/index";
import { mobile } from "./mobile/index";
import { views } from "./views/index";
import { reports } from "./reports/index";
import { setup } from "./setup/index";
import { areaNotes } from "./area-notes/index";
import { activityLog } from "./activity-log/index";
import { areas } from "./areas/index";
import { tours } from "./tours/index";
import { GuardTours } from "./guard-tours/index";
import { GenericTable } from "./generic-table/index";
import { manualRaise } from "./manual-raise/index";
import { responses } from "./responses/index";
import mobileRaiseTemplates from "./mobile-raise-templates";
import { license } from "./license/index";
import { lockdown } from "./lockdown/index";
import { videoReporting } from "./video-reporting/index";
import { sync } from "./sync/index";
import { externalMapLayers } from "./external-map-layers/index";
import { settings } from "./settings/index";
import { metadata } from "./metadata/index";
import { mapSetup } from "./map-setup/index";
import { caseManagement } from "./case-management/index";
import { manualaction } from "./manual-actions/index";
import { guardChat } from "./guard-chat";
import { externalMenuLink } from "./external-menu-link/index";
import { regions } from "./regions/index";

const debug = process.env.NODE_ENV !== "production";

Vue.use(Vuex);

// @todo
// 2. thinking we could have a section of state called `UI` that would represent the state of various UI elements
// (e.g. look at all the state properties that end with `Open`)
// 3. thinking about creating CCure, Groups, Event, EventShare modules

export default new Vuex.Store({
	state: getDefaultState(),
	getters: {
		...generalGetters
	},
	mutations: {
		...generalMutations
	},
	actions: {
		...generalActions
	},
	modules: {
		caseManagement,
		eventCategorySetup,
		eventqueue,
		armDisarm,
		siteMonitor,
		tasks,
		dispatch,
		userManagement,
		siteMonitorCameras,
		sessions,
		mapLayers,
		eventSearch,
		devices,
		alarmPoints,
		mobile,
		views,
		reports,
		activityLog,
		setup,
		areas,
		tours,
		GuardTours,
		areaNotes,
		GenericTable,
		manualRaise,
		responses,
		mobileRaiseTemplates,
		license,
		lockdown,
		videoReporting,
		sync,
		externalMapLayers,
		metadata,
		settings,
		mapSetup,
		manualaction,
		guardChat,
		externalMenuLink,
		regions,
		ldapManagement
	},
	plugins: [
		// @ref https://github.com/robinvdvleuten/vuex-persistedstate
		createPersistedState({
			paths: [
				"timeZones",
				"ulTypes",
				"regions.regions",
				"regions.xsrfTokens",
				"regions.login",
				"eventqueue.alarmQueueFooter",
				"eventqueue.hideMapFlag",
				"siteMonitor.hideMapFlag"
			]
		}),
		/**
		 * By default, the persisted state plugin uses localStorage which is shared amongst windows on the same
		 * domain. This causes issues when the user picks up a regional alarm while they have another window
		 * open (e.g, Views), as the region login will override the user data
		 * (and therefore the user ID/permissions/features).
		 *
		 * To fix this, we store the user and featuresList data in sessionStorage, which is only available in the
		 * current window and any windows it .open's.
		 */
		createPersistedState({
			paths: ["User", "featuresList"],
			storage: window.sessionStorage
		}),
		postLoginAppBootstrap,
		createMutationsSharer({
			predicate: [
				"logout",
				"setFeaturesList",
				"SetTimeZones",
				"SetUlTypes",
				"setMediaMatrixState",
				"siteMonitor/setIsController",
				"siteMonitor/setActiveMapItems",
				"siteMonitor/setActiveMapItemsRequired",
				"siteMonitor/setMapCircleCenter",
				"siteMonitor/setSelectedEventRecord",
				"siteMonitor/setActivity",
				"siteMonitorCameras/setAwaitingCamera",
				"siteMonitorCameras/setLinkRecord",
				"siteMonitorCameras/setCameraLink",
				"siteMonitorCameras/setPushContents",
				"siteMonitorCameras/setShownAuditClips",
				"tours/setAllTours",
				"tours/unsetTour",
				"tours/setNewTour",
				"tours/updateTour",
				"regions/setRegions",
				"regions/setXsrfToken",
				"regions/clearXsrfToken",
				"regions/setLogin",
				"regions/clearLogin",
				"regions/clearLogins",
				"regions/incrementLoginReferenceCount",
				"regions/decrementLoginReferenceCount",
				"views/setViewDimensions",
				"views/setSelectedViewTitle",
				"views/setSelectedViewCells",
				"views/deleteCell",
				"views/deletePageCells",
				"views/setSelectedTileType"
			],
			sharingKey: "vuex-mutations-sharer",
			storageKey: "vuex-mutation-sharer-storage"
		})
	],
	strict: debug
});
